/**
 * AI consent state — lifted out of `useAIConsent`'s per-component
 * `useState` into a shared Zustand store. Why:
 *
 *   1. **Mutation propagation without a JS reload.** Before this store,
 *      consent state lived inside the hook. The cache was the only
 *      shared source of truth — components read it on mount via
 *      `useEffect(loadConsent)` and never re-read it. The only way to
 *      propagate a consent change to mounted components was to tear
 *      the React tree down via `DevSettings.reload()`, which has been
 *      the proximate cause of the iOS sim XCTAutomationSession wedge
 *      that takes out entire E2E suites
 *      (run 2026-05-31T18-27-44-789Z, post CONV-009).
 *
 *   2. **Multi-consumer consistency.** With per-component state, two
 *      mounted `useAIConsent` instances could legitimately disagree on
 *      consent state (one mounted pre-revoke, one mounted post-revoke
 *      but before any cache invalidation). The store makes them
 *      subscribers to a single source of truth.
 *
 *   3. **Future-proofing for server-pushed changes.** When/if we wire
 *      a "consent revoked elsewhere" WS event, the handler updates the
 *      store and every UI reflects immediately. No remount choreography.
 *
 * The `useAIConsent` hook keeps its current public API — callers don't
 * change. It now just selects state from this store + exposes the
 * actions verbatim.
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { UserProfileCacheStorage } from '../generated/sync/sync-config';
import { createLogger } from '../utils/logger';
import { useAppBannerStore } from '../hooks/useAppBanner';

const log = createLogger('aiConsentStore');

/**
 * Bump when AI providers change — forces re-consent. Must match
 * CURRENT_CONSENT_VERSION on the server. Exposed for the hook to
 * derive `needsReConsent`.
 */
export const CURRENT_CONSENT_VERSION = 1;

interface AIConsentState {
  /**
   * `true` when consent is granted at the current version,
   * `false` when explicitly revoked,
   * `null` before `hydrate()` has run (still loading from cache).
   *
   * Distinguishing `null` (loading) from `false` (revoked) matters
   * for the consent modal — we don't want to flash the modal
   * during initial cache reads if the user is actually consented.
   */
  granted: boolean | null;
  /** ISO timestamp of when consent was granted; null if not consented. */
  grantedAt: string | null;
  /** Version at the time of grant. Used to detect provider changes. */
  version: number;
  /** True while the initial cache hydrate is in flight. */
  isLoading: boolean;

  // ── Actions ──────────────────────────────────────────────────

  /**
   * Load consent state from the profile cache. Idempotent — safe to
   * call from every `useAIConsent` instance's mount effect; subsequent
   * calls after the first hydration are cheap re-reads.
   */
  hydrate: () => Promise<void>;

  /**
   * Server-sync grant. Returns `true` if the sync succeeded (and the
   * store is now reflecting the granted state), `false` if the sync
   * failed (the store is unchanged and an error banner is surfaced).
   *
   * The boolean return is critical for callers that gate UI flow on
   * consent — e.g. closing the consent modal AND navigating into a
   * paid surface. A `false` return must NOT proceed, otherwise the
   * downstream consent-gated calls will 403.
   */
  grantConsent: () => Promise<boolean>;

  /**
   * Server-sync revoke. Doesn't return success/failure because callers
   * (the profile screen's revoke button) treat failures via the
   * error banner — there's no gated next step that depends on the
   * revoke succeeding synchronously.
   */
  revokeConsent: () => Promise<void>;
}

export const useAIConsentStore = create<AIConsentState>((set) => ({
  granted: null,
  grantedAt: null,
  version: 0,
  isLoading: true,

  hydrate: async () => {
    // We could short-circuit if `granted !== null`, but the cache may
    // have changed since the last hydrate (e.g. another part of the
    // app called `UserProfileCacheStorage.save` after the server
    // returned a fresh profile). Cheap re-read keeps the store
    // consistent with the cache without forcing every hook instance
    // to coordinate.
    try {
      const profile = await UserProfileCacheStorage.load();
      if (
        profile?.aiConsentGrantedAt &&
        profile?.aiConsentVersion === CURRENT_CONSENT_VERSION
      ) {
        set({
          granted: true,
          grantedAt: profile.aiConsentGrantedAt as string,
          version: profile.aiConsentVersion as number,
          isLoading: false,
        });
      } else if (profile) {
        // Profile exists but consent is missing or stale-versioned —
        // explicitly revoked or never granted at the current version.
        set({
          granted: false,
          grantedAt: null,
          version: (profile.aiConsentVersion as number) ?? 0,
          isLoading: false,
        });
      } else {
        // No profile cache yet — still "unknown" pending first fetch.
        set({ granted: null, grantedAt: null, version: 0, isLoading: false });
      }
    } catch (error) {
      log.warn('Failed to hydrate consent from profile cache', { error });
      set({ isLoading: false });
    }
  },

  grantConsent: async (): Promise<boolean> => {
    const result = await api.syncUser({
      aiConsent: { granted: true, version: CURRENT_CONSENT_VERSION },
    });

    if (result.ok) {
      // Server returns the full profile with server-computed consent
      // fields — write through to the cache (so a future hydrate or
      // app restart sees the granted state) and update the store.
      await UserProfileCacheStorage.save(result.data);
      const grantedAt = result.data.aiConsentGrantedAt ?? new Date().toISOString();
      set({
        granted: true,
        grantedAt,
        version: CURRENT_CONSENT_VERSION,
        isLoading: false,
      });
      log.info('AI consent granted');
      return true;
    }

    log.error('Failed to sync AI consent to server', new Error(result.kind), {
      kind: result.kind,
      error: 'error' in result ? result.error : undefined,
    });
    useAppBannerStore.getState().showBanner({
      message: 'Failed to save consent. Please try again.',
    });
    return false;
  },

  revokeConsent: async (): Promise<void> => {
    const result = await api.syncUser({
      // version MUST be >= 1 — the server-side Zod schema rejects
      // version: 0 (see backend SyncUserRequestSchema). The previous
      // hook implementation sent version: 0 and the resulting 400
      // failed silently in the UI; the dev handler worked around
      // that by sending CURRENT_CONSENT_VERSION verbatim. Using the
      // current version here is the correct contract — it means
      // "I'm declining at the current provider set".
      aiConsent: { granted: false, version: CURRENT_CONSENT_VERSION },
    });

    if (result.ok) {
      await UserProfileCacheStorage.save(result.data);
      set({ granted: false, grantedAt: null, version: 0, isLoading: false });
      log.info('AI consent revoked');
    } else {
      log.error('Failed to revoke AI consent on server', new Error(result.kind), {
        kind: result.kind,
        error: 'error' in result ? result.error : undefined,
      });
      useAppBannerStore.getState().showBanner({
        message: 'Failed to revoke consent. Please try again.',
      });
    }
  },
}));
